import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { usePopup } from '../../contexts/PopupContext';
import { supabase } from '../../lib/supabase';
import { getFriends } from '../../utils/friendsService';
import {
  applyDiscoverEventFilters,
  defaultMapDiscoverFilters,
  dismissDiscoverFilterChip,
  getMapDatePresetRange,
  mapDiscoverFiltersActive,
} from '../../utils/discoverEventFilters';
import { buildMapActiveFilterChips } from '../../utils/mapActiveFilterChips';
import EventCard from './EventCard';
import Loading from '../layout/Loading';
import MapActiveFilterChips from './map/MapActiveFilterChips';
import './ListView.css';

function ListView({ 
  mapVisibleEvents = null, 
  events: externalEvents = null, 
  isLoading: externalLoading = false,
  pageSizeOverride = null, 
  hidePagination = false, 
  compact = false 
}) {
  const { t } = useTranslation('discover');
  const { openPopup } = usePopup();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState(defaultMapDiscoverFilters());
  const [currentUserId, setCurrentUserId] = useState(null);
  const [friendIdSet, setFriendIdSet] = useState(new Set());
  const pageSize = pageSizeOverride || 20;
  const [isMapFiltered, setIsMapFiltered] = useState(false);
  const useExternalEvents = externalEvents !== null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (cancelled) return;
      setCurrentUserId(user?.id ?? null);
      if (!user?.id) {
        setFriendIdSet(new Set());
        return;
      }
      const fr = await getFriends(user.id);
      if (cancelled) return;
      if (fr.success && Array.isArray(fr.data)) {
        setFriendIdSet(new Set(fr.data.map((f) => f.id)));
      } else {
        setFriendIdSet(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const clientSourceEvents = useMemo(() => {
    if (useExternalEvents && Array.isArray(externalEvents)) {
      return externalEvents;
    }
    if (!useExternalEvents && Array.isArray(mapVisibleEvents)) {
      return mapVisibleEvents;
    }
    return null;
  }, [useExternalEvents, externalEvents, mapVisibleEvents]);

  // Filter events client-side based on filters
  const applyFiltersToEvents = useCallback((eventsToFilter) => {
    return applyDiscoverEventFilters(eventsToFilter, filters, {
      friendIds: friendIdSet,
      currentUserId,
    });
  }, [filters, friendIdSet, currentUserId]);

  const filteredClientEvents = useMemo(() => {
    if (!clientSourceEvents) {
      return null;
    }
    return applyFiltersToEvents(clientSourceEvents);
  }, [clientSourceEvents, applyFiltersToEvents]);

  const clientTotalCount = filteredClientEvents ? filteredClientEvents.length : 0;
  const clientTotalPages = filteredClientEvents
    ? Math.max(1, Math.ceil(clientTotalCount / pageSize))
    : totalPages;
  const clientPageEvents = useMemo(() => {
    if (!filteredClientEvents) {
      return null;
    }
    const startIndex = (currentPage - 1) * pageSize;
    return filteredClientEvents.slice(startIndex, startIndex + pageSize);
  }, [filteredClientEvents, currentPage, pageSize]);

  // Handle event arrays passed from the map/parent. Pagination is derived from currentPage.
  useEffect(() => {
    if (filteredClientEvents) {
      setIsMapFiltered(!useExternalEvents);
      setTotalCount(clientTotalCount);
      setTotalPages(clientTotalPages);
      setCurrentPage(1);
      setLoading(useExternalEvents ? externalLoading : false);
      setError(null);
    }
  }, [filteredClientEvents, clientTotalCount, clientTotalPages, useExternalEvents, externalLoading]);

  // Load events from map if mapVisibleEvents is provided
  useEffect(() => {
    if (!useExternalEvents && mapVisibleEvents === null) {
      // Normal load if no map events
      setIsMapFiltered(false);
    }
  }, [useExternalEvents, mapVisibleEvents]);

  const loadEvents = useCallback(async (pageNum) => {
    // Lists passed from the map/parent are paginated in render from currentPage.
    if (filteredClientEvents) {
      setLoading(useExternalEvents ? externalLoading : false);
      setError(null);
      return;
    }

    // If showing map-filtered events, apply filters client-side
    if (isMapFiltered && mapVisibleEvents) {
      const filteredEvents = applyFiltersToEvents(mapVisibleEvents);
      const startIndex = (pageNum - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      setEvents(filteredEvents.slice(startIndex, endIndex));
      setTotalCount(filteredEvents.length);
      setTotalPages(Math.ceil(filteredEvents.length / pageSize));
      setCurrentPage(pageNum);
      return;
    }

    // Wydarzenia z rodzica (np. zakładka Lista na Discover - ta sama pula co mapa)
    if (useExternalEvents && Array.isArray(externalEvents)) {
      const filteredEvents = applyFiltersToEvents(externalEvents);
      const startIndex = (pageNum - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      setEvents(filteredEvents.slice(startIndex, endIndex));
      setTotalCount(filteredEvents.length);
      setTotalPages(Math.max(1, Math.ceil(filteredEvents.length / pageSize)));
      setCurrentPage(pageNum);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get current date to filter only upcoming events (unless dateFrom filter is set)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayString = today.toISOString().split('T')[0];

      // Helper function to build filtered query
      const buildFilteredQuery = (baseQuery) => {
        let query = baseQuery;

        const datePresetRange =
          filters.datePreset && filters.datePreset !== 'custom'
            ? getMapDatePresetRange(filters.datePreset)
            : null;
        const effectiveDateFrom = datePresetRange?.from || filters.dateFrom;
        const effectiveDateTo = datePresetRange?.to || filters.dateTo;

        // Date filters
        if (effectiveDateFrom) {
          query = query.gte('event_date', effectiveDateFrom);
        } else {
          // Default: only upcoming events
          query = query.gte('event_date', todayString);
        }

        if (effectiveDateTo) {
          query = query.lte('event_date', effectiveDateTo);
        }

        // Free/Paid filter
        if (filters.isFree !== null && filters.isFree !== undefined) {
          query = query.eq('is_free', filters.isFree === true);
        }

        // Participants filter
        if (filters.minParticipants) {
          const min = parseInt(filters.minParticipants);
          if (!isNaN(min)) {
            query = query.gte('max_participants', min);
          }
        }

        if (filters.maxParticipants) {
          const max = parseInt(filters.maxParticipants);
          if (!isNaN(max)) {
            query = query.lte('max_participants', max);
          }
        }

        if (filters.mineOnly && currentUserId) {
          query = query.eq('host_id', currentUserId);
        }

        return query;
      };

      // Build base query for counting
      let countQuery = supabase
        .from('events')
        .select('*', { count: 'exact', head: true });

      // Try to add is_cancelled filter, but don't fail if column doesn't exist
      try {
        countQuery = countQuery.eq('is_cancelled', false);
      } catch (err) {
        // Column doesn't exist yet, continue without filter
      }

      countQuery = buildFilteredQuery(countQuery);

      let { count, error: countError } = await countQuery;

      // If error is about missing column, retry without filter
      if (countError && countError.message && countError.message.includes('is_cancelled')) {
        countQuery = supabase
          .from('events')
          .select('*', { count: 'exact', head: true });
        countQuery = buildFilteredQuery(countQuery);
        const retryResult = await countQuery;
        count = retryResult.count;
        countError = retryResult.error;
      }

      if (countError) {
        console.error('Error counting events:', countError);
      } else {
        setTotalCount(count || 0);
        setTotalPages(Math.ceil((count || 0) / pageSize) || 1);
      }

      // Calculate pagination range
      const from = (pageNum - 1) * pageSize;
      const to = from + pageSize - 1;

      // Fetch events from Supabase with pagination and filters
      let query = supabase
        .from('events')
        .select('*');

      // Try to add is_cancelled filter, but don't fail if column doesn't exist
      try {
        query = query.eq('is_cancelled', false);
      } catch (err) {
        // Column doesn't exist yet, continue without filter
      }

      query = buildFilteredQuery(query);
      query = query
        .order('event_date', { ascending: true })
        .order('start_time', { ascending: true })
        .range(from, to);

      let { data, error: fetchError } = await query;

      // If error is about missing column, retry without filter
      if (fetchError && fetchError.message && fetchError.message.includes('is_cancelled')) {
        query = supabase
          .from('events')
          .select('*');
        query = buildFilteredQuery(query);
        query = query
          .order('event_date', { ascending: true })
          .order('start_time', { ascending: true })
          .range(from, to);
        const retryResult = await query;
        data = retryResult.data;
        fetchError = retryResult.error;
      }

      if (fetchError) throw fetchError;

      // Filter out cancelled events if is_cancelled field exists
      const filteredData = (data || []).filter(event => event.is_cancelled !== true);
      setEvents(filteredData);
    } catch (err) {
      console.error('Error loading events:', err);
      setError(err.message || t('discover.list.error'));
    } finally {
      setLoading(false);
    }
  }, [
    pageSize,
    t,
    filters,
    isMapFiltered,
    mapVisibleEvents,
    applyFiltersToEvents,
    useExternalEvents,
    externalEvents,
    externalLoading,
    filteredClientEvents,
    currentUserId,
  ]);

  const isInitialMountRef = useRef(true);

  // Initial load z Supabase albo oznaczenie montażu przy wydarzeniach z rodzica (Discover)
  useEffect(() => {
    if (!isInitialMountRef.current) return;
    if (!mapVisibleEvents && !useExternalEvents) {
      isInitialMountRef.current = false;
      setCurrentPage(1);
      loadEvents(1);
    } else if (useExternalEvents) {
      isInitialMountRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useExternalEvents]);

  // Reset to page 1 when filters change and reload
  useEffect(() => {
    if (!isInitialMountRef.current) {
      setCurrentPage(1);
      loadEvents(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Load events when page changes (but not on initial mount or filter change)
  useEffect(() => {
    if (!isInitialMountRef.current && currentPage > 0) {
      loadEvents(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleOpenFilters = () => {
    openPopup('event-filters', {
      initialFilters: filters,
      onApply: (newFilters) => {
        setFilters(newFilters);
      }
    }, 0);
  };

  const hasActiveFilters = () => {
    return mapDiscoverFiltersActive(filters);
  };

  const activeFilterChips = useMemo(
    () => buildMapActiveFilterChips({ searchCenter: null, searchAddress: '', searchRange: '', mapDiscoverFilters: filters }, t),
    [filters, t]
  );

  const handleDismissFilterChip = useCallback((chipId) => {
    setFilters((prev) => dismissDiscoverFilterChip(prev, chipId));
  }, []);

  const displayedEvents = clientPageEvents !== null ? clientPageEvents : events;
  const displayedTotalCount = filteredClientEvents ? clientTotalCount : totalCount;
  const displayedTotalPages = filteredClientEvents ? clientTotalPages : totalPages;

  if (loading) {
    return (
      <div className="list-view">
        <Loading 
          className="list-view-loading" 
          size="medium"
          message={t('discover.list.loading')}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="list-view">
        <div className="list-view-error">
          <p>{error}</p>
          <button onClick={loadEvents} className="retry-button">
            {t('discover.list.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (displayedEvents.length === 0 && !hasActiveFilters()) {
    return (
      <div className="list-view">
        <div className="list-view-empty">
          <h2>{t('discover.list.empty.title')}</h2>
          <p>{t('discover.list.empty.description')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`list-view${compact ? ' compact' : ''}`}>
      <div className="list-view-header">
        <div className="list-view-header-left">
          <p className="list-view-count">
            {displayedTotalCount > 0 ? displayedTotalCount : displayedEvents.length} {t('discover.list.eventsCount')}
          </p>
          {hasActiveFilters() && (
            <span className="list-view-filters-active">
              {t('discover.list.filtersActive', 'Filtry aktywne')}
            </span>
          )}
        </div>
        <button
          className="filter-button"
          onClick={handleOpenFilters}
          title={t('discover.list.filter', 'Filtruj')}
        >
          <svg className="filter-button-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
            <path d="M4 6h16M8 12h8M10 18h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="6" cy="6" r="1.75" fill="currentColor" />
            <circle cx="16" cy="12" r="1.75" fill="currentColor" />
            <circle cx="14" cy="18" r="1.75" fill="currentColor" />
          </svg>
          {t('discover.list.filter', 'Filtruj')}
          {hasActiveFilters() && <span className="filter-badge">•</span>}
        </button>
      </div>
      {hasActiveFilters() && (
        <MapActiveFilterChips chips={activeFilterChips} onDismiss={handleDismissFilterChip} />
      )}
      <div className="events-list">
        {displayedEvents.length > 0 ? (
          displayedEvents.map((event) => <EventCard key={event.id} event={event} />)
        ) : (
          <div className="list-view-empty">
            <p>{t('events.filters.noResults', 'Brak wydarzeń spełniających wybrane kryteria')}</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!hidePagination && displayedTotalPages > 1 && (
        <div className="events-pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1 || loading}
          >
            ← {t('discover.list.pagination.previous') || t('notifications.previous') || 'Poprzednia'}
          </button>
          
          <div className="pagination-info">
            <span>
              {t('discover.list.pagination.page') || t('notifications.page') || 'Strona'} {currentPage} {t('discover.list.pagination.of') || t('notifications.of') || 'z'} {displayedTotalPages}
            </span>
            {displayedTotalCount > 0 && (
              <span className="pagination-total">
                ({displayedTotalCount} {t('discover.list.pagination.total') || t('notifications.total') || 'łącznie'})
              </span>
            )}
          </div>
          
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(displayedTotalPages, prev + 1))}
            disabled={currentPage === displayedTotalPages || loading}
          >
            {t('discover.list.pagination.next') || t('notifications.next') || 'Następna'} →
          </button>
        </div>
      )}
    </div>
  );
}

export default ListView;






